import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export default function SSOCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  
  // Prevent strict mode double-firing
  const hasFired = useRef(false);

  useEffect(() => {
    if (hasFired.current) return;
    hasFired.current = true;

    const token = searchParams.get('token');
    const state = searchParams.get('state');
    const savedState = sessionStorage.getItem('sso_state');

    if (!token || !state) {
      setError('Missing token or state in callback.');
      return;
    }

    if (state !== savedState) {
      setError('Invalid state parameter. Possible CSRF attack.');
      return;
    }

    const verifyToken = async () => {
      try {
        const res = await api.post('/auth/sso-callback', { token });
        const data = res.data;

        if (data && data.access_token) {
          login(data.access_token, {
            id: data.id,
            name: data.name,
            email: data.email,
            role: data.role,
            highest_plan_name: data.highest_plan_name,
            highest_tier_level: data.highest_tier_level,
            mustChangePassword: data.mustChangePassword,
            avatarUrl: data.avatarUrl,
            points: data.points,
            sso_hub_url: data.sso_hub_url,
          });
          
          sessionStorage.removeItem('sso_state');
          navigate('/dashboard');
        } else {
          setError('Invalid SSO response.');
        }
      } catch (err: any) {
        console.error('SSO Callback error:', err);
        setError(err.response?.data?.error || 'Authentication failed. Please try again.');
      }
    };

    verifyToken();
  }, [searchParams, navigate, login]);

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full text-center">
        {error ? (
          <div className="bg-red-50 border border-red-200 p-6 rounded-2xl shadow-sm">
            <span className="material-symbols-outlined text-red-500 text-4xl mb-4">error</span>
            <h3 className="text-lg font-black text-slate-900 mb-2">Authentication Failed</h3>
            <p className="text-sm text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold uppercase text-xs tracking-widest hover:bg-slate-800 transition-colors"
            >
              Return to Login
            </button>
          </div>
        ) : (
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-slate-200 border-t-primary rounded-full animate-spin mb-6"></div>
            <p className="text-slate-600 font-bold tracking-widest uppercase text-sm">Verifying Authentication...</p>
          </div>
        )}
      </div>
    </div>
  );
}
